import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Autocomplete,
  IconButton,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSelector } from "react-redux";
import { useNotification } from "../../hooks/useNotification";
import moment from "moment";
import { reScheduleCallApi } from "../../api/callScheduleApi";
import { fetchCouncilorList } from "../../api/councilorApi";

const callTypes = ["video","voice","whatsapp"];

export default function RescheduleCallModal({refetch, open, onClose }) {
  const scheduleCallSlice = useSelector(state=>state.schedulecall)
  const [saving,setSaving] = useState(false)
  const  { showSuccessNotification, showErrorNotification } = useNotification()
  const [selectedCouncilor, setSelectedCouncilor] = useState(null);
  const [councilorList, setCouncilorList] = useState([]);
  const [callDate, setCallDate] = useState(null);
  const [callTime, setCallTime] = useState(null);
  const [callType, setCallType] = useState("");
  const [notes, setNotes] = useState("");

  const handleSubmit = () => {
    if (!callDate || !callTime) {
      showErrorNotification('Date and time are required for the call.');
      return;
    }
    const date = moment(callDate).format("YYYY-MM-DD");
    const time = moment(callTime).format("HH:mm");
    const payload = {
      _id:scheduleCallSlice?.selectedScheduleCall?.id,
      counselorId: selectedCouncilor?.value,
      notes,
      callType,
      dateTime: moment(`${date} ${time}`, "YYYY-MM-DD HH:mm").format("YYYY-MM-DD HH:mm")
    };

    reScheduleCallApi({ setSaving, payload, showSuccessNotification, showErrorNotification })
      .then(res => { onClose(); refetch() })
  };

  const fetchCounsilor = () => {
      fetchCouncilorList({ search: "" })
        .then(res => {
          setCouncilorList(res?.data || [])
        }).catch(error => {
          showErrorNotification(error?.message)
        })
    }

    useEffect(()=>{
      fetchCounsilor()
    },[])

  useEffect(() => {
    setSelectedCouncilor(null)
    setCallDate(null)
    setCallTime(null)
    setCallType(null)
    setNotes(null)

  }, [open])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      {/* Title */}
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 600, fontSize: "20px", color: "#15243E" }}>
          Reschedule Call
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Student Details */}
        <Box mb={2}>
          <Typography sx={{ fontWeight: 500, fontSize: "14px", color: "#363853", py: 0.5 }}>
            Student: <Typography variant="span" sx={{ fontWeight: "normal" }}>{scheduleCallSlice?.selectedScheduleCall?.studentDetails}</Typography>
          </Typography>
          <Typography sx={{ fontWeight: 500, fontSize: "14px", color: "#363853", py: 0.5 }}>
            Course: <Typography variant="span" sx={{ fontWeight: "normal" }}>{scheduleCallSlice?.selectedScheduleCall?.universityName}</Typography>
          </Typography>
          <Typography sx={{ fontWeight: 500, fontSize: "14px", color: "#363853", py: 0.5 }}>
            Preferred: <Typography variant="span" sx={{ fontWeight: "normal" }}>{scheduleCallSlice?.selectedScheduleCall?.preferredDate}</Typography> at <Typography variant="span" sx={{ fontWeight: "normal" }}>{scheduleCallSlice?.selectedScheduleCall?.preferredTime}</Typography>
          </Typography>
        </Box>

        {/* Assign Councilor */}
        <Box mb={2}>
          <Typography sx={{ fontWeight: 500, fontSize: "14px", color: "#363853", mb: 1 }}>
            Assign counselor
          </Typography>
          <Autocomplete
            options={councilorList}
            getOptionLabel={(option) => option.label}
            value={selectedCouncilor}
            onChange={(event, newValue) => setSelectedCouncilor(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search counselor..."
                fullWidth
                variant="outlined"
                sx={{
                  "& .MuiInputBase-root": { height: 40, fontSize: "14px" },
                  "& .MuiInputBase-input::placeholder": { fontSize: "14px", color: "#6E7485" },
                }}
              />
            )} 
          />
        </Box>

       <LocalizationProvider dateAdapter={AdapterDateFns}>
  <Box
    mb={2}
    display="flex"
    gap={2}          // space between Date and Time
    alignItems="center" // vertical alignment
  >
    {/* Call Date */}
    <Box flex={1}>
      <Typography
        sx={{ fontWeight: 500, fontSize: "14px", color: "#363853", mb: 1 }}
      >
        Call Date
      </Typography>
      <DatePicker
       minDate={new Date()}
        value={callDate}
        onChange={(newValue) => setCallDate(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                minHeight: 40,
                height: 40,
                "& input": { fontSize: "14px", padding: "0 14px" },
              },
            }}
          />
        )}
         sx={{
               "& .MuiPickersInputBase-root": { height: 40, fontSize: "14px" },
            }}
      />
    </Box>

    {/* Call Time */}
    <Box flex={1}>
      <Typography
        sx={{ fontWeight: 500, fontSize: "14px", color: "#363853", mb: 1 }}
      >
        Call Time
      </Typography>
      <TimePicker
        value={callTime}
        onChange={(newValue) => setCallTime(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                minHeight: 40,
                height: 40,
                "& input": { fontSize: "14px", padding: "0 14px" },
                "& svg": { fontSize: 20 },
              },
            }}
          />
        )}
        sx={{
               "& .MuiPickersInputBase-root": { height: 40, fontSize: "14px" },
            }}
      />
    </Box>
  </Box>
</LocalizationProvider>


        {/* Call Type */}
        <Box mb={2}>
          <Typography sx={{ fontWeight: 500, fontSize: "14px", color: "#363853", mb: 1 }}>
            Call Type
          </Typography>
       <TextField
  select
  fullWidth
  value={callType}
  onChange={(e) => setCallType(e.target.value)}
  variant="outlined"
  sx={{
    "& .MuiInputBase-root": {
      height: 40,
      fontSize: "14px",
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      height: "100%",
    },
    "& .MuiMenuItem-root": {
      fontSize: "14px",
      minHeight: 40,
    },
    textTransform:'capitalize'
  }}
  SelectProps={{
    displayEmpty: true,
    renderValue: (selected) => {
      if (!selected) {
        return <span style={{ color: "gray" }}>Select Call Type</span>;
      }
      return selected;
    },
  }}
>
  {callTypes.map((type) => (
    <MenuItem key={type} value={type} sx={{textTransform:'capitalize'}}>
      {type}
    </MenuItem>
  ))}
</TextField>



        </Box>

        {/* Internal Notes */}
        <Box mb={2}>
          <Typography sx={{ fontWeight: 500, fontSize: "14px", color: "#363853", mb: 1 }}>
            Internal Notes (Optional)
          </Typography>
          <TextField
            placeholder="Enter notes about the call"
            fullWidth
            multiline
            minRows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            variant="outlined"
            sx={{
              "& .MuiInputBase-root": { fontSize: "14px" },
              "& .MuiInputBase-input::placeholder": { fontSize: "14px", color: "#6E7485" },
            }}
          />
        </Box>
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions sx={{ px: 3, pb: 3, gap: 1, }}>
        <Button onClick={onClose} variant="outlined" sx={{ width: "100%", height: "50px" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          loading={saving}
          disabled={!selectedCouncilor || !callDate || !callTime || !callType || saving}
          sx={{ background: "#1075FF", width: "100%", height: "50px", "&:hover": { background: "#0d63d6" } }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
